import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

// Paths the OAuth redirect is allowed to send the user to after success.
// Only relative, internal paths accepted — prevents open-redirect attacks.
const ALLOWED_REDIRECT_PATHS = new Set([
  '/meeting-plan',
  '/dashboard',
  '/settings',
]);

function safeRedirectPath(raw: string | null): string {
  if (!raw) return '/meeting-plan';
  try {
    // Must be a relative path matching known routes
    if (raw.startsWith('/') && !raw.startsWith('//') && ALLOWED_REDIRECT_PATHS.has(raw)) {
      return raw;
    }
  } catch { /* fall through */ }
  return '/meeting-plan';
}

const GoogleCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing Google Calendar authorization...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code  = searchParams.get('code');
        const error = searchParams.get('error');
        const state = searchParams.get('state');

        if (error) {
          setStatus('error');
          setMessage('Authorization was denied or failed.');
          setTimeout(() => navigate('/meeting-plan'), 3000);
          return;
        }

        if (!code) {
          setStatus('error');
          setMessage('No authorization code received.');
          setTimeout(() => navigate('/meeting-plan'), 3000);
          return;
        }

        // Retrieve and clear the CSRF state we set when initiating the OAuth flow
        const expectedState = sessionStorage.getItem('oauth_state_google');
        sessionStorage.removeItem('oauth_state_google');

        if (!expectedState || state !== expectedState) {
          setStatus('error');
          setMessage('Invalid state parameter. Please try again.');
          setTimeout(() => navigate('/meeting-plan'), 3000);
          return;
        }

        // Exchange code on the backend — client secret never touches the browser.
        // Send only {code, redirect_uri} — the backend already matched the state
        // (it issued it when the client called /init).
        const tokenRes = await fetch('/api/oauth/google/token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code,
            redirect_uri: '/auth/google/callback',
          }),
        });

        if (!tokenRes.ok) {
          setStatus('error');
          setMessage('Failed to complete authorization. Please try again.');
          setTimeout(() => navigate('/meeting-plan'), 3000);
          return;
        }

        const { access_token, refresh_token } = await tokenRes.json() as {
          access_token: string;
          refresh_token: string | null;
        };

        // Persist only connection metadata — tokens stored in DB, not localStorage
        if (user) {
          await supabase.from('calendar_connections').upsert({
            user_id:       user.id,
            provider:      'google',
            access_token,
            refresh_token: refresh_token ?? null,
            connected_at:  new Date().toISOString(),
            last_sync:     null,
          }, { onConflict: 'user_id,provider' });
        }

        // Mark connected in sessionStorage (non-sensitive flag only)
        sessionStorage.setItem('calendar_connected_google', 'true');

        setStatus('success');
        setMessage('Successfully connected to Google Calendar!');

        setTimeout(() => {
          const redirectPath = safeRedirectPath(sessionStorage.getItem('calendar_redirect_after'));
          sessionStorage.removeItem('calendar_redirect_after');
          sessionStorage.removeItem('calendar_connection_type');
          navigate(redirectPath);
        }, 2000);

      } catch {
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again.');
        setTimeout(() => navigate('/meeting-plan'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100vh', padding: '20px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      <div style={{
        background: 'white', padding: '40px', borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
        maxWidth: '500px', width: '100%', textAlign: 'center',
      }}>
        {status === 'processing' && (
          <>
            <div style={{
              width: '60px', height: '60px', border: '4px solid #f3f4f6',
              borderTopColor: '#3b82f6', borderRadius: '50%',
              margin: '0 auto 20px', animation: 'spin 1s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {status === 'success' && (
          <div style={{
            width: '60px', height: '60px', background: '#dcfce7', borderRadius: '50%',
            margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
        )}

        {status === 'error' && (
          <div style={{
            width: '60px', height: '60px', background: '#fee2e2', borderRadius: '50%',
            margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="3">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </div>
        )}

        <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 12px 0' }}>
          {status === 'processing' && 'Connecting...'}
          {status === 'success'    && 'Success!'}
          {status === 'error'      && 'Error'}
        </h2>
        <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>{message}</p>
      </div>
    </div>
  );
};

export default GoogleCallback;
